import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, Flag } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export function ReportButton({ profile, className }) {
  const { lang } = useLang();
  if (!profile) return null;
  return (
    <button onClick={(e) => { e.stopPropagation(); document.dispatchEvent(new CustomEvent('open-report', { detail: profile })); }}
      data-testid="report-btn" className={className || "flex items-center gap-2 text-xs t-text-muted hover:text-red-500 hover:bg-red-500/5 transition py-2 px-3 rounded-xl"}>
      <Flag className="w-3.5 h-3.5" />{lang === 'en' ? 'Report' : 'Laporkan'}
    </button>
  );
}

export function ReportDialog() {
  const { token, API } = useAuth();
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [screenshot, setScreenshot] = useState('');
  const [truth, setTruth] = useState(false);
  const [loading, setLoading] = useState(false);

  // Listen for open-report events from any page
  React.useEffect(() => {
    const handler = (e) => {
      setTarget(e.detail);
      setReason('');
      setScreenshot('');
      setTruth(false);
      setOpen(true);
    };
    document.addEventListener('open-report', handler);
    return () => document.removeEventListener('open-report', handler);
  }, []);

  const handleSubmit = async () => {
    if (!target || !reason.trim() || !truth) return;
    setLoading(true);
    try {
      await axios.post(`${API}/members/report`, {
        reported_member_id: target.id,
        reported_member_name: target.name || 'Unknown',
        reason,
        screenshot,
        truthfulness_agreed: true,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(lang === 'en' ? 'Report submitted' : 'Laporan dikirim');
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setLoading(false);
  };

  const handleScreenshot = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setScreenshot(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="glass-card t-text max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="t-text flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            {lang === 'en' ? 'Report Member' : 'Laporkan Anggota'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-3">
          {target && (
            <div className="glass-card rounded-xl p-3">
              <p className="t-text-muted text-xs">{lang === 'en' ? 'Reporting:' : 'Melaporkan:'}</p>
              <p className="t-text font-semibold text-sm">{target.name || 'Unknown'} (ID: {target.id?.slice(0, 8)})</p>
            </div>
          )}

          <div>
            <label className="t-text-secondary text-xs uppercase tracking-wider block mb-1.5">{lang === 'en' ? 'Reason for report' : 'Alasan laporan'}</label>
            <Textarea data-testid="report-reason" value={reason} onChange={e => setReason(e.target.value)}
              className="t-bg-input t-border rounded-lg text-sm px-4 py-3 t-text" rows={3}
              placeholder={lang === 'en' ? 'Describe the harassment or suspected fraud...' : 'Jelaskan pelecehan atau dugaan penipuan...'} />
          </div>

          <div>
            <label className="t-text-secondary text-xs uppercase tracking-wider block mb-1.5">{lang === 'en' ? 'Screenshot evidence (optional)' : 'Bukti screenshot (opsional)'}</label>
            <input type="file" accept="image/*" onChange={handleScreenshot} data-testid="report-screenshot"
              className="w-full t-bg-input t-border rounded-lg text-sm px-4 py-2 t-text" />
            {screenshot && <img src={screenshot} alt="evidence" className="mt-2 w-full max-h-32 object-cover rounded-lg" />}
          </div>

          <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl glass-card" data-testid="report-truth-check">
            <div className={`w-5 h-5 rounded-md border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-all ${truth ? 'bg-[#D98A94] border-[#D98A94]' : 't-border'}`}
              onClick={() => setTruth(!truth)}>
              {truth && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
            </div>
            <span className="t-text text-xs leading-relaxed">
              {lang === 'en'
                ? 'I truthfully declare this information is accurate and I am ready to be held accountable if the information I have provided is false.'
                : 'Saya dengan jujur menyatakan bahwa informasi ini akurat dan saya siap bertanggung jawab jika informasi yang saya berikan adalah palsu.'}
            </span>
          </label>

          <div className="flex gap-3">
            <Button onClick={() => setOpen(false)} variant="outline" className="flex-1 t-border t-text-secondary rounded-lg" data-testid="report-cancel-btn">
              {lang === 'en' ? 'Cancel' : 'Batal'}
            </Button>
            <Button onClick={handleSubmit} disabled={loading || !reason.trim() || !truth}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-40" data-testid="report-submit-btn">
              {loading ? '...' : (lang === 'en' ? 'Submit Report' : 'Kirim Laporan')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
